import React, { useEffect, useMemo, useState } from 'react';
import { tradingApi } from '../services/api';
import { Zap, Moon, Sun, Activity, TrendingUp } from 'lucide-react';
import { useStrategyStatusQuery } from '../store/tradingApiSlice';
import StrategyMarginStatus from './StrategyMarginStatus';

interface StrategyState {
    enabled: boolean;
    hydrated: boolean;
    runtimeStale: boolean;
    statusLoaded: boolean;
    symbols: string[];
    sleeveAllocation?: number;
    universeSize?: number;
}

const InvestorStrategyView: React.FC = () => {
    const onStatus = useStrategyStatusQuery('ON', { pollingInterval: 30000 }).data;
    const orbStatus = useStrategyStatusQuery('ORB', { pollingInterval: 30000 }).data;
    const dtStatus = useStrategyStatusQuery('DT', { pollingInterval: 30000 }).data;
    const nqmmStatus = useStrategyStatusQuery('NQMM', { pollingInterval: 30000 }).data;
    const [configs, setConfigs] = useState<Record<string, any>>({});

    useEffect(() => {
        const fetchConfigs = async () => {
            try {
                const [onConfig, orbConfig, dtConfig, nqmmConfig] = await Promise.all([
                    tradingApi.getStrategyDbConfig('ON'),
                    tradingApi.getStrategyDbConfig('ORB'),
                    tradingApi.getStrategyDbConfig('DT'),
                    tradingApi.getStrategyDbConfig('NQMM'),
                ]);
                setConfigs({ ON: onConfig, ORB: orbConfig, DT: dtConfig, NQMM: nqmmConfig });
            } catch (error) {
                console.warn('Failed to fetch strategy configuration for investor view', error);
            }
        };
        fetchConfigs();
        const interval = setInterval(fetchConfigs, 30000);
        return () => clearInterval(interval);
    }, []);

    const strategies = useMemo<Record<string, StrategyState>>(() => {
        const getSymbols = (name: string, status: any) => {
            const active = status?.data?.selected_symbols || status?.data?.active_symbols;
            if (name === 'NQMM' && Array.isArray(active) && active.length > 0) return active;
            const configured = configs[name]?.data?.config?.symbols;
            if (Array.isArray(configured) && configured.length > 0) return configured;
            return Array.isArray(active) ? active : [];
        };
        return {
            ON: {
                enabled: Boolean(onStatus?.data?.enabled),
                hydrated: Boolean(onStatus?.data?.hydrated),
                runtimeStale: Boolean(onStatus?.data?.runtime_stale),
                statusLoaded: Boolean(onStatus?.success && onStatus?.data),
                symbols: getSymbols('ON', onStatus),
            },
            ORB: {
                enabled: Boolean(orbStatus?.data?.enabled),
                hydrated: Boolean(orbStatus?.data?.hydrated),
                runtimeStale: Boolean(orbStatus?.data?.runtime_stale),
                statusLoaded: Boolean(orbStatus?.success && orbStatus?.data),
                symbols: getSymbols('ORB', orbStatus),
            },
            DT: {
                enabled: Boolean(dtStatus?.data?.enabled),
                hydrated: Boolean(dtStatus?.data?.hydrated),
                runtimeStale: Boolean(dtStatus?.data?.runtime_stale),
                statusLoaded: Boolean(dtStatus?.success && dtStatus?.data),
                symbols: getSymbols('DT', dtStatus),
            },
            NQMM: {
                enabled: Boolean(nqmmStatus?.data?.enabled),
                hydrated: Boolean(nqmmStatus?.data?.hydrated),
                runtimeStale: Boolean(nqmmStatus?.data?.runtime_stale),
                statusLoaded: Boolean(nqmmStatus?.success && nqmmStatus?.data),
                symbols: getSymbols('NQMM', nqmmStatus),
                sleeveAllocation: nqmmStatus?.data?.sleeve_allocation ?? nqmmStatus?.data?.position_size,
                universeSize: nqmmStatus?.data?.universe_size ?? configs.NQMM?.data?.config?.constituent_count,
            },
        };
    }, [configs, dtStatus, nqmmStatus, onStatus, orbStatus]);

    const StatusBadge = ({ strategy }: { strategy: StrategyState }) => {
        const label = !strategy.statusLoaded
            ? 'Unavailable'
            : !strategy.enabled
            ? 'Disabled'
            : strategy.runtimeStale
            ? 'Stale'
            : !strategy.hydrated
            ? 'Starting'
            : 'Enabled';
        const active = strategy.statusLoaded && strategy.enabled && !strategy.runtimeStale;
        const className = !strategy.statusLoaded
            ? 'bg-white/5 text-muted border-white/5'
            : !strategy.enabled
            ? 'bg-white/5 text-muted border-white/5'
            : strategy.runtimeStale
            ? 'bg-warning-500/10 text-warning-400 border-warning-500/20'
            : 'bg-success-500/10 text-success-500 border-success-500/20';

        return (
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${className}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-success-500 animate-pulse' : 'bg-gray-500'}`} />
                {label}
            </div>
        );
    };

    const SymbolTags = ({ symbols }: { symbols: string[] }) => (
        <div className="flex flex-wrap gap-1.5 mt-3">
            {symbols.map(sym => (
                <span key={sym} className="px-1.5 py-0.5 rounded-sm bg-white/5 border border-white/5 text-[10px] font-mono text-muted">
                    {sym}
                </span>
            ))}
        </div>
    );

    return (
        <div className="panel full-bleed-panel">
            <div className="p-6 border-b border-white/10">
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg bg-neon/10 text-neon">
                        <Activity className="w-5 h-5" />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-white">Strategy Overview</h2>
                        <p className="text-sm text-muted">Configured automated systems and their current runtime status.</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-white/10">
                {/* ORB Strategy */}
                <div className="p-6 space-y-4 hover:bg-white/2 transition duration-300">
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-indigo-500/20 text-indigo-400">
                                <Zap className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="font-bold text-white">Opening Range Breakout</h3>
                                <div className="text-xs font-mono text-muted mt-0.5">STRATEGY: ORB</div>
                            </div>
                        </div>
                        <StatusBadge strategy={strategies.ORB} />
                    </div>

                    <p className="text-sm text-gray-400 leading-relaxed">
                        An intraday strategy that seeks to participate in strong moves after the market
                        opens, with defined risk controls and no overnight exposure.
                    </p>
                    <SymbolTags symbols={strategies.ORB.symbols} />
                    <StrategyMarginStatus status={orbStatus?.data} compact />

                    <div className="pt-2">
                        <h4 className="text-xs font-semibold text-muted uppercase tracking-wider mb-2">Key Features</h4>
                        <ul className="grid grid-cols-2 gap-2">
                            <li className="text-xs text-indigo-200/70 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-indigo-400/50"></span>
                                Morning Volatility
                            </li>
                            <li className="text-xs text-indigo-200/70 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-indigo-400/50"></span>
                                Momentum Based
                            </li>
                            <li className="text-xs text-indigo-200/70 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-indigo-400/50"></span>
                                Strict Stop Loss
                            </li>
                            <li className="text-xs text-indigo-200/70 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-indigo-400/50"></span>
                                No Overnight Risk
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Day Trade Strategy */}
                <div className="p-6 space-y-4 hover:bg-white/2 transition duration-300">
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-orange-500/20 text-orange-400">
                                <Sun className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="font-bold text-white">Intraday Trend</h3>
                                <div className="text-xs font-mono text-muted mt-0.5">STRATEGY: DT</div>
                            </div>
                        </div>
                        <StatusBadge strategy={strategies.DT} />
                    </div>

                    <p className="text-sm text-gray-400 leading-relaxed">
                        An intraday trend-following strategy that responds to market movement during the
                        session and closes positions before the day ends.
                    </p>
                    <SymbolTags symbols={strategies.DT.symbols} />
                    <StrategyMarginStatus status={dtStatus?.data} compact />

                    <div className="pt-2">
                        <h4 className="text-xs font-semibold text-muted uppercase tracking-wider mb-2">Key Features</h4>
                        <ul className="grid grid-cols-2 gap-2">
                            <li className="text-xs text-orange-200/70 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-orange-400/50"></span>
                                Trend Following
                            </li>
                            <li className="text-xs text-orange-200/70 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-orange-400/50"></span>
                                Volatility Adaptive
                            </li>
                            <li className="text-xs text-orange-200/70 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-orange-400/50"></span>
                                Dynamic Risk Mgmt
                            </li>
                            <li className="text-xs text-orange-200/70 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-orange-400/50"></span>
                                EOD Exit
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Overnight Strategy */}
                <div className="p-6 space-y-4 hover:bg-white/2 transition duration-300">
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-purple-500/20 text-purple-400">
                                <Moon className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="font-bold text-white">Overnight Swing</h3>
                                <div className="text-xs font-mono text-muted mt-0.5">STRATEGY: ON</div>
                            </div>
                        </div>
                        <StatusBadge strategy={strategies.ON} />
                    </div>

                    <p className="text-sm text-gray-400 leading-relaxed">
                        A long overnight strategy designed to capture moves between market sessions,
                        with positions closed during the following trading session.
                    </p>
                    <SymbolTags symbols={strategies.ON.symbols} />
                    <StrategyMarginStatus status={onStatus?.data} compact />

                    <div className="pt-2">
                        <h4 className="text-xs font-semibold text-muted uppercase tracking-wider mb-2">Key Features</h4>
                        <ul className="grid grid-cols-2 gap-2">
                            <li className="text-xs text-purple-200/70 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-purple-400/50"></span>
                                Overnight Edge
                            </li>
                            <li className="text-xs text-purple-200/70 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-purple-400/50"></span>
                                Mean Reversion
                            </li>
                            <li className="text-xs text-purple-200/70 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-purple-400/50"></span>
                                Diversified
                            </li>
                            <li className="text-xs text-purple-200/70 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-purple-400/50"></span>
                                Gap Capture
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Nasdaq-100 Monthly Momentum Strategy */}
                <div className="p-6 space-y-4 hover:bg-white/2 transition duration-300">
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-success-500/20 text-success-500">
                                <TrendingUp className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="font-bold text-white">Nasdaq-100 Momentum</h3>
                                <div className="text-xs font-mono text-muted mt-0.5">STRATEGY: NQMM</div>
                            </div>
                        </div>
                        <StatusBadge strategy={strategies.NQMM} />
                    </div>

                    <p className="text-sm text-gray-400 leading-relaxed">
                        A long-only portfolio sleeve that ranks Nasdaq-100 stocks by relative momentum
                        and periodically reallocates toward stronger names.
                    </p>
                    {strategies.NQMM.universeSize !== undefined && (
                        <div className="text-[10px] font-mono text-success-500/80">Database QQQ universe: {strategies.NQMM.universeSize} constituents</div>
                    )}
                    {strategies.NQMM.symbols.length > 0 && <SymbolTags symbols={strategies.NQMM.symbols.slice(0, 12)} />}
                    {strategies.NQMM.symbols.length > 12 && <div className="text-[10px] text-muted -mt-2">+{strategies.NQMM.symbols.length - 12} more active names</div>}
                    <StrategyMarginStatus status={nqmmStatus?.data} compact />

                    <div className="pt-2">
                        <h4 className="text-xs font-semibold text-muted uppercase tracking-wider mb-2">Key Features</h4>
                        <ul className="grid grid-cols-2 gap-2">
                            <li className="text-xs text-success-500/70 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-success-500/50"></span>
                                Monthly Rebalance
                            </li>
                            <li className="text-xs text-success-500/70 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-success-500/50"></span>
                                Cross-Sectional Rank
                            </li>
                            <li className="text-xs text-success-500/70 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-success-500/50"></span>
                                Portfolio Sleeve
                            </li>
                            <li className="text-xs text-success-500/70 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-success-500/50"></span>
                                Long Only
                            </li>
                        </ul>
                        {strategies.NQMM.sleeveAllocation !== undefined && (
                            <div className="mt-3 text-[10px] font-mono text-success-500/80">
                                Sleeve allocation: {(strategies.NQMM.sleeveAllocation * 100).toFixed(1)}%
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvestorStrategyView;
